import { Repo } from '../data/schema.js';
import { TAG_DEFS, CERT_DEFS, TAG_SHOW_MIN, tagCap, tagName, tagDecay } from '../data/tags.js';

/**
 * 标签系统：累加 / 当天上限 / 衰减 / 资格证查询。
 *
 * 数据落在 db.tags 表，主键 key = `${tag}.${src}`：
 * 每个来源单独计算当日上限（「今天翻垃圾已加过 2 点 dirty」），免得一时刷满。
 * tags: { key, tag, src, value, day, updated }
 * flags:`cap:${day}:${tag}:${src}` -> 今天已加多少（数值看 capSourceOf）
 */
export const DEFAULT_CAP_PER_DAY = 10;

function table(db) { return db.table('tags'); }

export const Tags = {
  /** 单个来源当前的累加值 */
  value(db, tag, src = 'global') {
    const r = table(db).find(`${tag}.${src}`);
    return r ? r.value : 0;
  },

  /** 所有来源求和（资格证也是一个标签：有就有值，没有就是 0） */
  total(db, tag) {
    let n = 0;
    for (const r of table(db).all()) if (r.tag === tag) n += r.value;
    return n;
  },

  /**
   * 真正的对外读取入口：身上某个标签有几层、总共多少点。
   * @returns {{total:number, stage:number, capped:boolean}}
   */
  value2(db, tag) {
    return this.get(db, tag);
  },

  /** 同 value2，简写 */
  get(db, tag) {
    const total = Math.min(tagCap(tag), Math.round(this.total(db, tag)));
    return {
      total,
      stage: total >= 30 ? (total >= 60 ? (total >= 90 ? (total >= 120 ? 5 : 4) : 3) : 2) : 1,
      capped: total >= tagCap(tag),
    };
  },

  /** 累加：当日上限内加，超了就截至 */
  gain(db, tag, amount = 1, src = 'global', capPerDay = DEFAULT_CAP_PER_DAY) {
    if (!tag || amount <= 0) return { added: 0, blockedByCap: false };
    const w = Repo.getWorld(db);
    const key = `${tag}.${src}`;
    const todayKey = `cap:${w.day}:${tag}:${src}`;
    const used = Number(Repo.getFlag(db, todayKey)) || 0;
    const roomInDay = Math.max(0, capPerDay - used);
    const rowCost = Math.min(amount, roomInDay);
    if (rowCost <= 0) return { added: 0, blockedByCap: true, capPerDay };

    const t = table(db);
    const row = t.find(key);
    const nextRow = Math.min(tagCap(tag), (row ? row.value : 0) + rowCost);
    const toAdd = nextRow - (row ? row.value : 0);
    if (row) t.update(key, { value: nextRow, day: w.day, updated: w.dayCount });
    else t.insert({ key, tag, src, value: nextRow, day: w.day, updated: w.dayCount });
    Repo.setFlag(db, todayKey, used + toAdd);
    return { added: toAdd, blockedByCap: used + toAdd >= capPerDay, capPerDay };
  },

  /** 直接减，不清当日上限 */
  reduce(db, tag, amount = 1) {
    if (!tag || amount <= 0) return 0;
    let left = amount;
    const t = table(db);
    for (const row of t.where((r) => r.tag === tag && r.value > 0)) {
      const cut = Math.min(row.value, left);
      t.update(row.key, { value: row.value - cut });
      left -= cut;
      if (left <= 0) break;
    }
    return amount - left;
  },

  /** 每天离线推进时调用：全部上限清 0，各标签自然消退 */
  decayDaily(db) {
    const t = table(db);
    const seen = new Set();
    for (const r of t.all()) {
      const d = tagDecay(r.tag);
      if (!d) continue;
      seen.add(r.key);
      t.update(r.key, { value: Math.max(0, r.value - d) });
    }
    // 当日上限旗子：按 key 里记的日子自行过期，只留最近两天的
    const w = Repo.getWorld(db);
    const flags = db.table('flags');
    for (const f of flags.all()) {
      if (typeof f.key === 'string' && f.key.startsWith('cap:')) {
        const dayPart = Number(String(f.key).split(':')[1]);
        if (Number.isFinite(dayPart) && dayPart < w.day - 2) flags.remove(f.key);
      }
    }
    return seen.size;
  },

  /** 人物简述：给 UI 用，按累计数从大到小 */
  list(db, { min = TAG_SHOW_MIN, includeCerts = true } = {}) {
    const sums = new Map();
    for (const r of table(db).all()) sums.set(r.tag, (sums.get(r.tag) || 0) + r.value);
    const out = [];
    for (const [tag, v] of sums) {
      if (!TAG_DEFS[tag] && !(includeCerts && CERT_DEFS[tag])) continue;
      const total = Math.round(v);
      if (total < min && !CERT_DEFS[tag]) continue;
      out.push({ id: tag, name: tagName(tag), total, isCert: !!CERT_DEFS[tag] });
    }
    out.sort((a, b) => b.total - a.total);
    return out;
  },

  /** 资格证：只有「有没有」两态，达到了就有了 */
  cert(db, id) {
    return this.has(db, id);
  },

  has(db, id) {
    return this.total(db, id) > 0;
  },

  grantCert(db, id, src = 'system') {
    const key = `${id}.${src}`;
    const t = table(db);
    if (t.find(key)) return true;
    const w = Repo.getWorld(db);
    t.insert({ key, tag: id, src, value: 120, day: w.day, updated: w.dayCount });
    return true;
  },

  revokeCert(db, id) {
    const t = table(db);
    for (const r of t.where((r2) => r2.tag === id)) t.remove(r.key);
    return true;
  },

  certs(db) {
    const have = this.list(db, { min: 0, includeCerts: true }).filter((x) => x.isCert);
    const ids = new Set(have.map((h) => h.id));
    return have.map((h) => ({ ...h, name: CERT_DEFS[h.id] ? CERT_DEFS[h.id].name : h.name, owned: ids.has(h.id) }));
  },
};
